import { logger, LoggerParams } from "@/monitoring/utils/logger";

interface ILogActivitySubmissionErrorParams {
  message: string;
  context: {
    contentId: string;
    user: {
      id: string | number;
      unique_id: string;
    };
    url: string;
    assignmentId?: string | number | undefined;
    currentLevel?:
      | {
          id: string | number;
          header_id: number;
        }
      | undefined;
    activityId?: string | number | undefined;
    [param: string]: string | number | object | undefined;
  };
  error?: Error | undefined;
}

/**
 * Logs activity-related submission errors (such as quiz submissions,
 * write prompt submissions, annotations and power words) with structured
 * context information.
 *
 * This function wraps a `logger.warn` call, standardizing the shape of
 * logged data for easier debugging and monitoring.
 *
 * @param message - A descriptive error message.
 * @param context - Additional metadata providing context for the error.
 * @param context.contentId - The ID of the related content item.
 * @param context.user - Information about the user involved.
 * @param context.user.id - Numeric or string identifier for the user.
 * @param context.user.unique_id - Unique string identifier for the user.
 * @param context.url - The URL where the error occurred.
 * @param context.assignmentId - (Optional) ID of the assignment if applicable.
 * @param context.currentLevel - (Optional) Current level information.
 * @param context.currentLevel.id - ID of the current level.
 * @param context.currentLevel.header_id - Header ID associated with the level.
 * @param context.activityId - (Optional) ID of the related activity.
 * @param context.[param] - Any additional metadata key/value pairs.
 * @param error - (Optional) The Error object associated with the failure.
 *
 * @returns void
 */
const logActivitySubmissionError = ({
  message,
  error,
  context: {
    contentId,
    currentLevel,
    user,
    assignmentId,
    url,
    activityId,
    ...context
  },
}: ILogActivitySubmissionErrorParams) => {
  const params: LoggerParams = {
    message,
    error,
    context: {
      ...context,
      contentId,
      articleHeaderId: currentLevel?.header_id,
      currentLevelId: currentLevel?.id,
      userId: user.id,
      userUniqueId: user.unique_id,
      assignmentId,
      url,
      activityId,
    },
  };

  logger.warn(params);
};

export default logActivitySubmissionError;
